import { z } from "zod";

import type { TimeBucket } from "../entities/types";
import type { ChartOverride } from "./chart-derive";
import {
  applyExploreDatePreset,
  defaultExploreFilterState,
  type ExploreDateRangePreset,
  type ExploreFilterState,
  exploreDateRangeFromFilterState,
  matchesExploreDatePreset,
} from "./explore-filter-state";
import type { Filter } from "./types";

export const DEFAULT_EXPLORE_ENTITY = "test_results";
export const DEFAULT_EXPLORE_VIEW = "rows" as const;
export const DEFAULT_SUMMARY_MEASURE_ID = "count:*";
export const DEFAULT_SUMMARY_CHART = "auto" as const;

const exploreViewSchema = z.enum(["rows", "summary"]);
/** Legacy preset token when `since`/`until` are omitted. */
const exploreDatePresetSchema = z.enum([
  "7",
  "30",
  "prev_month",
  "90",
  "ytd",
  "365",
  "all",
]);
const isoDateSchema = z.iso.date();
const exploreChartSchema = z.enum(["auto", "bar", "line", "donut"]);
const exploreBucketSchema = z.enum(["day", "week", "month", "quarter", "year"]);

const filterOpSchema = z.enum([
  "eq",
  "neq",
  "gt",
  "lt",
  "gte",
  "lte",
  "contains",
  "startsWith",
  "endsWith",
  "notContains",
  "in",
  "notIn",
  "isNull",
  "isNotNull",
]);

const columnFilterSchema = z.object({
  field: z.string().min(1),
  op: filterOpSchema,
  value: z.unknown().optional(),
});

export const exploreSearchSchema = z.object({
  entity: z.string().min(1).optional(),
  view: exploreViewSchema.optional(),
  /** Legacy; ignored when `since` and `until` are both set. */
  date: exploreDatePresetSchema.optional(),
  since: isoDateSchema.optional(),
  until: isoDateSchema.optional(),
  cancelled: z
    .union([
      z.literal("1"),
      z.literal("0"),
      z.literal("true"),
      z.literal("false"),
    ])
    .optional(),
  group: z.string().min(1).optional(),
  bucket: exploreBucketSchema.optional(),
  measure: z.string().min(1).optional(),
  chart: exploreChartSchema.optional(),
  cf: z.string().min(1).optional(),
});

export type ExploreSearchParams = z.infer<typeof exploreSearchSchema>;

export type ExploreViewMode = z.infer<typeof exploreViewSchema>;

export type ExploreSummaryUrlState = {
  groupField: string;
  bucket: TimeBucket;
  measureId: string;
  chartOverride: Exclude<ChartOverride, "kpi" | null>;
};

export type ExploreUrlState = {
  entity: string;
  view: ExploreViewMode;
  filterState: ExploreFilterState;
  summary: ExploreSummaryUrlState;
};

const PREFERRED_SUMMARY_GROUP_FIELDS = [
  "client_org",
  "test_type",
  "test_spec",
  "accession_date",
] as const;

export function defaultSummaryGroupField(
  groupableFields: readonly string[],
): string {
  const preferred = PREFERRED_SUMMARY_GROUP_FIELDS.find((field) =>
    groupableFields.includes(field),
  );
  return preferred ?? groupableFields[0] ?? "";
}

export function defaultSummaryBucketForField(
  field: string,
  bucketableFields: ReadonlySet<string>,
): TimeBucket {
  return bucketableFields.has(field) ? "day" : "month";
}

export function defaultExploreUrlState(
  groupableFields: readonly string[] = [],
  bucketableFields: ReadonlySet<string> = new Set(),
): ExploreUrlState {
  const groupField = defaultSummaryGroupField(groupableFields);
  return {
    entity: DEFAULT_EXPLORE_ENTITY,
    view: DEFAULT_EXPLORE_VIEW,
    filterState: defaultExploreFilterState(),
    summary: {
      groupField,
      bucket: defaultSummaryBucketForField(groupField, bucketableFields),
      measureId: DEFAULT_SUMMARY_MEASURE_ID,
      chartOverride: DEFAULT_SUMMARY_CHART,
    },
  };
}

export function parseExploreSearchParams(
  raw: Record<string, unknown>,
): ExploreSearchParams {
  return exploreSearchSchema.parse(raw);
}

function filterStateFromSearch(
  search: ExploreSearchParams,
  defaults: ExploreFilterState,
): ExploreFilterState {
  if (search.since && search.until) {
    return {
      ...defaults,
      dateSince: search.since,
      dateUntil: search.until,
    };
  }

  if (search.date) {
    return applyExploreDatePreset(defaults, search.date);
  }

  return defaults;
}

export function exploreSearchToUrlState(
  search: ExploreSearchParams,
  options: {
    groupableFields: readonly string[];
    bucketableFields: ReadonlySet<string>;
    measureIds: ReadonlySet<string>;
    chartIds: ReadonlySet<string>;
    entityNames: ReadonlySet<string>;
  },
): ExploreUrlState {
  const defaults = defaultExploreUrlState(
    options.groupableFields,
    options.bucketableFields,
  );

  const entity =
    search.entity && options.entityNames.has(search.entity)
      ? search.entity
      : defaults.entity;

  const view = search.view ?? defaults.view;

  const filterState: ExploreFilterState = {
    ...filterStateFromSearch(search, defaults.filterState),
    includeCancelled: parseCancelledFlag(search.cancelled),
    columnFilters: decodeColumnFilters(search.cf),
  };

  let groupField = search.group ?? defaults.summary.groupField;
  if (!options.groupableFields.includes(groupField)) {
    groupField = defaultSummaryGroupField(options.groupableFields);
  }

  let bucket = search.bucket ?? defaults.summary.bucket;
  if (!options.bucketableFields.has(groupField)) {
    bucket = defaults.summary.bucket;
  }

  let measureId = search.measure ?? defaults.summary.measureId;
  if (!options.measureIds.has(measureId)) {
    measureId = DEFAULT_SUMMARY_MEASURE_ID;
  }

  let chartOverride = search.chart ?? defaults.summary.chartOverride;
  if (!options.chartIds.has(chartOverride)) {
    chartOverride = DEFAULT_SUMMARY_CHART;
  }

  return {
    entity,
    view,
    filterState,
    summary: { groupField, bucket, measureId, chartOverride },
  };
}

export function exploreUrlStateToSearchParams(
  state: ExploreUrlState,
  defaults: ExploreUrlState,
): ExploreSearchParams {
  const params: ExploreSearchParams = {};

  if (state.entity !== defaults.entity) {
    params.entity = state.entity;
  }
  if (state.view !== defaults.view) {
    params.view = state.view;
  }

  const range = exploreDateRangeFromFilterState(state.filterState);
  if (!range) {
    params.date = "all";
  } else if (!matchesExploreDatePreset(state.filterState, "30")) {
    params.since = range.since;
    params.until = range.until;
  }

  if (
    state.filterState.includeCancelled !== defaults.filterState.includeCancelled
  ) {
    params.cancelled = state.filterState.includeCancelled ? "1" : "0";
  }

  const cf = encodeColumnFilters(state.filterState.columnFilters);
  if (cf) {
    params.cf = cf;
  }

  if (state.view === "summary") {
    if (state.summary.groupField !== defaults.summary.groupField) {
      params.group = state.summary.groupField;
    }
    if (state.summary.bucket !== defaults.summary.bucket) {
      params.bucket = state.summary.bucket;
    }
    if (state.summary.measureId !== defaults.summary.measureId) {
      params.measure = state.summary.measureId;
    }
    if (state.summary.chartOverride !== defaults.summary.chartOverride) {
      params.chart = state.summary.chartOverride;
    }
  }

  return params;
}

const LEGACY_DATE_SEARCH_KEYS = [
  "date",
] as const satisfies readonly (keyof ExploreSearchParams)[];

const SUMMARY_SEARCH_KEYS = [
  "group",
  "bucket",
  "measure",
  "chart",
] as const satisfies readonly (keyof ExploreSearchParams)[];

/** Search patch for navigate: sets `undefined` to drop stale params from the URL. */
export function exploreUrlStateToNavigateSearch(
  state: ExploreUrlState,
  defaults: ExploreUrlState,
): ExploreSearchParams {
  const active = exploreUrlStateToSearchParams(state, defaults);
  const patch: ExploreSearchParams = { ...active };

  const range = exploreDateRangeFromFilterState(state.filterState);
  if (range) {
    patch.date = undefined;
  } else {
    patch.since = undefined;
    patch.until = undefined;
  }

  if (matchesExploreDatePreset(state.filterState, "30")) {
    patch.since = undefined;
    patch.until = undefined;
    patch.date = undefined;
  }

  if (state.filterState.columnFilters.length === 0) {
    patch.cf = undefined;
  }

  if (
    state.filterState.includeCancelled === defaults.filterState.includeCancelled
  ) {
    patch.cancelled = undefined;
  }

  if (state.entity === defaults.entity) {
    patch.entity = undefined;
  }
  if (state.view === defaults.view) {
    patch.view = undefined;
  }

  if (state.view !== "summary") {
    for (const key of SUMMARY_SEARCH_KEYS) {
      patch[key] = undefined;
    }
  }

  for (const key of LEGACY_DATE_SEARCH_KEYS) {
    if (!(key in active)) {
      patch[key] = undefined;
    }
  }

  return patch;
}

function parseCancelledFlag(value: ExploreSearchParams["cancelled"]): boolean {
  if (value === "1" || value === "true") {
    return true;
  }
  return false;
}

function decodeColumnFilters(encoded: string | undefined): Filter[] {
  if (!encoded) {
    return [];
  }

  try {
    const json = decodeBase64Url(encoded);
    const parsed = JSON.parse(json) as unknown;
    const result = z.array(columnFilterSchema).safeParse(parsed);
    return result.success ? (result.data as Filter[]) : [];
  } catch {
    return [];
  }
}

function encodeColumnFilters(filters: readonly Filter[]): string | undefined {
  if (filters.length === 0) {
    return undefined;
  }

  return encodeBase64Url(JSON.stringify(filters));
}

function encodeBase64Url(value: string): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "utf8").toString("base64url");
  }

  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/u, "");
}

function decodeBase64Url(value: string): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(value, "base64url").toString("utf8");
  }

  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "=",
  );
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// Re-export for callers that still import preset type from url-state flows.
export type { ExploreDateRangePreset };
